import * as React from "react";
import {
  Alert, Box, Button, MenuItem, Select, Snackbar, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { ArrowLeft, Download, FileText } from "lucide-react";
import {
  ACTIVE_BATCH_STATUSES, TERMINAL_BATCH_STATUSES, cancelBatch, getBatch, patchBatchItem, retryFailedBatch,
  saveBatchZip, startBatch, type Batch, type BatchItem,
} from "../../api/batches";
import { PageHeader } from "../Common/PageHeader";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { StatusChip } from "./StatusChip";
import { ItemReportDialog } from "./ItemReportDialog";
import { batchModeLabel } from "./BatchRow";
import { useLatestPoll } from "./useLatestPoll";

const POLL_MS = 3000;
const HANDLERS = ["invoice_audit", "run_skill"];

interface Props { batchId: string; onBack: () => void }

export function BatchDetail({ batchId, onBack }: Props) {
  const [batch, setBatch] = React.useState<Batch | null>(null);
  const [items, setItems] = React.useState<BatchItem[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [reportItem, setReportItem] = React.useState<BatchItem | null>(null);

  // Set by Cancel: keep polling until the batch reaches a terminal status so
  // the final state shows without a manual refresh.
  const [awaitingTerminal, setAwaitingTerminal] = React.useState(false);

  const active = !!batch && ACTIVE_BATCH_STATUSES.includes(batch.status);
  const polling = active || (awaitingTerminal && !!batch && !TERMINAL_BATCH_STATUSES.includes(batch.status));
  const refresh = useLatestPoll(
    () => getBatch(batchId),
    (d) => { setError(null); setBatch(d.batch); setItems(d.items); },
    (e) => setError(e instanceof Error ? e.message : String(e)),
    polling,
    POLL_MS,
  );

  React.useEffect(() => { void refresh(); }, [batchId, refresh]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      void refresh();
    } catch (e) {
      setToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const contracts = items.filter((i) => i.kind === "contract" && i.document_id != null);
  const hasHeld = items.some((i) => i.status === "needs_review");

  return (
    <Box>
      <PageHeader
        title={batch?.name || "Batch"}
        subtitle={batch ? `${batchModeLabel(batch)} · ${batch.total} files` : undefined}
        actions={
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button startIcon={<ArrowLeft size={16} />} onClick={onBack} data-testid="batch-back">
              All batches
            </Button>
            {batch && (hasHeld || batch.status === "draft") && (
              <Button variant="contained" disabled={busy} data-testid="batch-start"
                onClick={() => act(() => startBatch(batchId))}>
                Start
              </Button>
            )}
            {batch && batch.failed > 0 && !active && (
              <Button disabled={busy} data-testid="batch-retry-failed"
                onClick={() => act(() => retryFailedBatch(batchId))}>
                Retry failed
              </Button>
            )}
            {batch && (active || batch.status === "awaiting_review") && (
              <Button color="error" disabled={busy} data-testid="batch-cancel"
                onClick={() => { setAwaitingTerminal(true); void act(() => cancelBatch(batchId)); }}>
                Cancel
              </Button>
            )}
            {batch?.has_results && (
              <Button variant="outlined" startIcon={<Download size={16} />} disabled={busy}
                data-testid="batch-download"
                onClick={() => act(() => saveBatchZip(batchId, batch.name))}>
                Download zip
              </Button>
            )}
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {batch?.error && <Alert severity="error" sx={{ mb: 2 }}>{batch.error}</Alert>}
      {!error && !batch && <CardSkeletonList />}

      {batch && (
        <Stack direction="row" sx={{ mb: 2, alignItems: "center", gap: 1.5 }}>
          <StatusChip status={batch.status} />
          <Typography variant="body2" color="text.secondary">
            {batch.succeeded} done · {batch.failed} failed · {batch.held} held · {batch.cancelled} cancelled
          </Typography>
        </Stack>
      )}

      {batch && (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" data-testid="batch-plan-table">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Handler</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => {
                const review = it.status === "needs_review";
                return (
                  <TableRow key={it.id} data-testid="batch-item-row" data-item-id={it.id}>
                    <TableCell>
                      {it.filename}
                      {it.error && (
                        <Typography variant="caption" color="error" sx={{ display: "block" }}>
                          {it.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{it.kind}</TableCell>
                    <TableCell>
                      {review ? (
                        <Select size="small" displayEmpty value={it.handler ?? ""}
                          inputProps={{ "data-testid": "item-handler-select" }}
                          onChange={(e) => act(() => patchBatchItem(batchId, it.id, { handler: e.target.value || null }))}>
                          <MenuItem value=""><em>none</em></MenuItem>
                          {HANDLERS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                        </Select>
                      ) : (it.handler ?? "—")}
                    </TableCell>
                    <TableCell>
                      {review && it.kind === "invoice" ? (
                        <Select size="small" displayEmpty value={it.contract_document_id ?? ""}
                          inputProps={{ "data-testid": "item-contract-select" }}
                          onChange={(e) => {
                            const v = e.target.value as number | "";
                            act(() => patchBatchItem(batchId, it.id, { contract_document_id: v === "" ? null : v }));
                          }}>
                          <MenuItem value=""><em>no contract</em></MenuItem>
                          {contracts.map((c) => (
                            <MenuItem key={c.id} value={c.document_id as number}>{c.filename}</MenuItem>
                          ))}
                        </Select>
                      ) : (it.contract_filename ?? (it.contract_document_id != null ? `#${it.contract_document_id}` : "—"))}
                    </TableCell>
                    <TableCell>{Math.round(it.confidence * 100)}%</TableCell>
                    <TableCell><StatusChip status={it.status} testId="item-status-chip" /></TableCell>
                    <TableCell align="right">
                      {review && (
                        <Button size="small" color="warning" disabled={busy} data-testid="item-exclude"
                          onClick={() => act(() => patchBatchItem(batchId, it.id, { exclude: true }))}>
                          Exclude
                        </Button>
                      )}
                      {(it.status === "succeeded" || it.status === "failed") && (
                        <Button size="small" startIcon={<FileText size={14} />}
                          data-testid="item-report-button" onClick={() => setReportItem(it)}>
                          Report
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <ItemReportDialog
        batchId={batchId}
        itemId={reportItem?.id ?? null}
        filename={reportItem?.filename ?? ""}
        onClose={() => setReportItem(null)}
      />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
